import { useState } from "react";
import { toast } from "sonner";
import { Mail, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/** Where every sheet, brief, kit and overview email is sent. */
export const DeliveryEmails = () => {
  const { user, savePreferences } = useAuth();
  const prefs = user?.preferences || {};
  const list = prefs.delivery_emails || [];
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const write = async (next) => {
    setBusy(true);
    try {
      await savePreferences({ ...prefs, delivery_emails: next });
      toast.success(next.length ? "Delivery list updated" : "Back to your sign-in email");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const add = () => {
    const email = value.trim().toLowerCase();
    if (!email.includes("@") || email.length < 5) return toast.error("Enter a full email address");
    if (list.includes(email)) return toast.error("Already on the list");
    if (list.length >= 5) return toast.error("Five addresses is the limit");
    setValue("");
    write([...list, email]);
  };

  return (
    <div className="space-y-3" data-testid="delivery-emails-setting">
      <Label className="label-caps inline-flex items-center gap-2">
        <Mail className="h-3.5 w-3.5" /> Send my sheets, briefs and kits to
      </Label>
      <div className="flex flex-wrap gap-2" data-testid="delivery-emails-list">
        {list.map((email) => (
          <span
            key={email}
            data-testid={`delivery-email-${email}`}
            className="inline-flex items-center gap-2 border border-border rounded-full pl-3 pr-1.5 py-1 text-sm"
          >
            {email}
            <button
              data-testid={`delivery-email-remove-${email}`}
              disabled={busy}
              onClick={() => write(list.filter((e) => e !== email))}
              className="h-6 w-6 grid place-items-center rounded-full text-destructive hover:bg-destructive/10 transition-colors duration-200"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
        {list.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing set — everything goes to {user?.email}.
          </p>
        ) : null}
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          data-testid="delivery-email-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="clerk@chambers.in"
          className="rounded-sm h-11 sm:max-w-sm"
        />
        <Button
          variant="outline"
          data-testid="delivery-email-add-btn"
          disabled={busy}
          onClick={add}
          className="rounded-full h-11 px-5"
        >
          <Plus className="h-4 w-4 mr-2" /> Add address
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Every address gets a copy. Leave the list empty to use your sign-in email.
      </p>
    </div>
  );
};
